import { useRef, useState } from 'react';
import { apiFetch } from '../../../auth/apiFetch';

const apiBaseUrl = process.env.REACT_APP_BOOKCARD_URL || process.env.REACT_APP_API_URL || window.location.origin;

const stripHtml = (html) => (html || '').replace(/<[^>]+>/g, '').trim();

export function useBioEditor() {
  const [holders, setHolders] = useState([]);
  const [holdersLoading, setHoldersLoading] = useState(false);
  const [holdersError, setHoldersError] = useState(null);
  const [backCoverCopyLength, setBackCoverCopyLength] = useState(0);
  const [needsEditingThreshold, setNeedsEditingThreshold] = useState(0);

  const [bios, setBios] = useState({});

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const previewUrlRef = useRef(null);

  const setBioText = (copyrightHolderId, text) => {
    setBios((prev) => ({ ...prev, [copyrightHolderId]: text }));
  };

  const setAllBios = (map) => setBios(map);

  const estimatedLength = backCoverCopyLength
    + Object.values(bios).reduce((sum, bio) => sum + stripHtml(bio).length, 0);
  const likelyExceedsTwoPages = needsEditingThreshold > 0 && estimatedLength > needsEditingThreshold;

  const loadHolders = async (ean) => {
    setHoldersLoading(true);
    setHoldersError(null);
    try {
      const r = await apiFetch(`${apiBaseUrl}/api/bookcard/${ean}/copyright-holders`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setHolders(data.holders);
      setBackCoverCopyLength(data.backCoverCopyLength);
      setNeedsEditingThreshold(data.needsEditingThreshold);
      setBios(Object.fromEntries(data.holders.map((h) => [h.copyrightHolderId, h.bio || ''])));
      return data.holders;
    } catch (e) {
      setHoldersError(e.message);
      return [];
    } finally {
      setHoldersLoading(false);
    }
  };

  const updatePreview = async (ean, biosOverride) => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const r = await apiFetch(`${apiBaseUrl}/api/bookcard/${ean}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(biosOverride || bios),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch (e) {
      setPreviewError(e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const reset = () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;
    setHolders([]);
    setBackCoverCopyLength(0);
    setNeedsEditingThreshold(0);
    setBios({});
    setPreviewUrl(null);
    setPreviewError(null);
  };

  return {
    holders, holdersLoading, holdersError, loadHolders,
    bios, setBioText, setAllBios,
    estimatedLength, needsEditingThreshold, likelyExceedsTwoPages,
    previewUrl, previewLoading, previewError, updatePreview,
    reset,
  };
}
